import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthService } from '../AuthService';
import type { UserProfile } from '../types';

interface ProfileScreenProps {
  onBack?: () => void;
}

const PASSWORD_MIN_LENGTH = 8;

function passwordProblem(pwd: string): string | null {
  if (pwd.length < PASSWORD_MIN_LENGTH) {
    return `New password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  if (!/[a-zA-Z]/.test(pwd) || !/[0-9]/.test(pwd)) {
    return 'New password must contain both letters and numbers';
  }
  return null;
}

export const ProfileScreen: React.FC<ProfileScreenProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  // Editable details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSaved, setDetailsSaved] = useState(false);

  // Password change
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSaved, setPwdSaved] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await AuthService.getMe();
      setProfile(data);
      setFirstName(data.first_name || '');
      setLastName(data.last_name || '');
      setPhone(data.phone_number || '');
    } catch (e: any) {
      setLoadError(e?.message || 'Could not load your profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const detailsDirty = useMemo(() => {
    if (!profile) return false;
    return (
      firstName !== (profile.first_name || '') ||
      lastName !== (profile.last_name || '') ||
      phone !== (profile.phone_number || '')
    );
  }, [profile, firstName, lastName, phone]);

  const handleSaveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailsDirty || savingDetails) return;
    setSavingDetails(true);
    setDetailsError(null);
    setDetailsSaved(false);
    try {
      const updated = await AuthService.updateMe({
        first_name: firstName,
        last_name: lastName,
        phone_number: phone,
      });
      setProfile((p) => (p ? { ...p, ...updated } : p));
      setDetailsSaved(true);
    } catch (e: any) {
      setDetailsError(e?.message || 'Could not save your details.');
    } finally {
      setSavingDetails(false);
    }
  };

  const passwordsMatch = newPwd.length > 0 && newPwd === confirmPwd;
  const pwdValidationMsg = newPwd ? passwordProblem(newPwd) : null;
  const canSubmitPwd =
    !!oldPwd && !!newPwd && !!confirmPwd && passwordsMatch && !pwdValidationMsg && !savingPwd;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitPwd) return;
    setSavingPwd(true);
    setPwdError(null);
    setPwdSaved(false);
    try {
      await AuthService.updateMe({ old_password: oldPwd, new_password: newPwd });
      setPwdSaved(true);
      setOldPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (e: any) {
      setPwdError(e?.message || 'Could not change your password.');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <main className="card form-card" role="main" aria-labelledby="profileTitle">
      <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
      <h1 id="profileTitle">Account settings</h1>
      {profile?.username && (
        <p className="desc">
          Signed in as <strong>{profile.username}</strong>
          {profile.email ? <> · {profile.email}</> : null}
        </p>
      )}

      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <span className="spinner" aria-hidden="true" /> Loading your profile…
        </div>
      ) : loadError ? (
        <div aria-live="polite">
          <div className="alert error" role="alert">{loadError}</div>
          <div className="actions" style={{ marginTop: '12px' }}>
            <button type="button" className="btn" onClick={loadProfile}>Retry</button>
          </div>
        </div>
      ) : (
        <>
          {/* --- Personal details --- */}
          <form onSubmit={handleSaveDetails} className="form" aria-label="Profile details">
            <div aria-live="polite">
              {detailsError && <div className="alert error" role="alert">{detailsError}</div>}
              {detailsSaved && !detailsDirty && (
                <div className="alert" role="status" style={{ color: '#106b38' }}>
                  Your details have been saved.
                </div>
              )}
            </div>

            <label>
              <span>First name</span>
              <input
                type="text"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setDetailsSaved(false); }}
                autoComplete="given-name"
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                type="text"
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setDetailsSaved(false); }}
                autoComplete="family-name"
              />
            </label>
            <label>
              <span>Phone number</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setDetailsSaved(false); }}
                autoComplete="tel"
                placeholder="e.g. 0123456789"
              />
            </label>

            <div className="actions" style={{ marginTop: '16px' }}>
              <button className="btn primary" type="submit" disabled={!detailsDirty || savingDetails}>
                {savingDetails ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span className="spinner" aria-hidden="true" /> Saving…
                  </span>
                ) : 'Save details'}
              </button>
            </div>
          </form>

          <hr style={{ width: '100%', border: 0, borderTop: '1px solid var(--line, #e2e8f0)', margin: '24px 0' }} />

          {/* --- Change password --- */}
          <form onSubmit={handleChangePassword} className="form" aria-label="Change password">
            <h2 style={{ fontSize: '1.05rem', margin: '0 0 4px' }}>Change password</h2>
            <div aria-live="polite">
              {pwdError && <div className="alert error" role="alert">{pwdError}</div>}
              {pwdSaved && (
                <div className="alert" role="status" style={{ color: '#106b38' }}>
                  Your password has been changed.
                </div>
              )}
            </div>

            <label>
              <span>Current password</span>
              <div className="pw-field">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={oldPwd}
                  onChange={(e) => { setOldPwd(e.target.value); setPwdSaved(false); }}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="btn tiny"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-pressed={showPwd}
                  aria-label={showPwd ? 'Hide passwords' : 'Show passwords'}
                >
                  {showPwd ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>
            <label>
              <span>New password</span>
              <input
                type={showPwd ? 'text' : 'password'}
                value={newPwd}
                onChange={(e) => { setNewPwd(e.target.value); setPwdSaved(false); }}
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
              />
              {newPwd && pwdValidationMsg && (
                <small style={{ color: '#c41e3a', fontWeight: 600 }}>{pwdValidationMsg}</small>
              )}
              {!pwdValidationMsg && (
                <small style={{ color: 'var(--muted)', marginTop: '4px' }}>
                  At least {PASSWORD_MIN_LENGTH} characters with a mix of letters and numbers.
                </small>
              )}
            </label>
            <label>
              <span>Confirm new password</span>
              <input
                type={showPwd ? 'text' : 'password'}
                value={confirmPwd}
                onChange={(e) => { setConfirmPwd(e.target.value); setPwdSaved(false); }}
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
              />
              {confirmPwd && !passwordsMatch && (
                <small style={{ color: '#c41e3a', fontWeight: 600 }}>Passwords do not match</small>
              )}
            </label>

            <div className="actions" style={{ marginTop: '16px' }}>
              <button className="btn primary" type="submit" disabled={!canSubmitPwd}>
                {savingPwd ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span className="spinner" aria-hidden="true" /> Updating…
                  </span>
                ) : 'Change password'}
              </button>
            </div>
          </form>

          {onBack && (
            <div className="below-form" style={{ textAlign: 'center', marginTop: '16px' }}>
              <button type="button" className="link-btn" onClick={onBack}>
                Back to deals
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default ProfileScreen;
